import styled from "@emotion/styled";
import { Box, Card, CardContent, Grid, Stack, Typography } from "@mui/material";
import { getAlarmName, getColorByName } from "utils/AppUtil";


// Define the desired alarm order shown on cards
const ALARM_ORDER = ["All", "Critical", "Major", "Minor", "Warning"];

const normalizeAlarmName = (name = "") => name.replaceAll("Alarms", "").trim();

// Function to sort alarms based on desired order
const sortAlarms = (alarms) => {
    if (!alarms || !Array.isArray(alarms)) return alarms;

    return [...alarms]
        .filter((alarm) => normalizeAlarmName(alarm?.name) !== "Informational")
        .sort((a, b) => {
        const aName = normalizeAlarmName(a.name);
        const bName = normalizeAlarmName(b.name);
        
        const aIndex = ALARM_ORDER.indexOf(aName);
        const bIndex = ALARM_ORDER.indexOf(bName);
        
        // If both are in the order, sort by their position
        if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
        }
        
        // If only one is in the order, it goes first
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        
            // If neither is in the order, maintain relative order
            return 0;
        });
};

// Styled components



const StyledImageAlarmCard = styled(Card)(({ bgcolor, imageIndex }) => ({
    backgroundColor: bgcolor || "#ffffff",
    color: "white",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    // borderLeft: { sm: "5px solid #a9b2bd", xs: "5px solid #a9b2bd" },
    alignItems: "center",
    width: "100%",
    height: "auto", // Auto height for small screens
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    borderTopLeftRadius: "14px",
    borderBottomLeftRadius: "14px",
    cursor: "pointer",
    "& .MuiCardContent-root": {
        width: "100%",
        padding: "16px",
        display: "flex",
        justifyContent: "start",
        alignItems: "center",
    },
    // Small screens (xs, sm) - auto height
    "@media (max-width: 959px)": {
        height: "auto",
        borderTopRightRadius: "14px",
        borderBottomRightRadius: "14px",
        marginBottom: "8px",
        marginTop: imageIndex > 0 ? "1rem" : 0
    },
    // Medium screens and up (md, lg, xl) - 5.5rem height
    "@media (min-width: 960px)": {
        height: "5.5rem",
    },
}));



const StyledAlarmCard = styled(Card)(({ bgcolor, isLast }) => ({
    backgroundColor: bgcolor || "#ffffff",
    color: "white",
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "flex-start",
    textAlign: "start",
    width: "100%",
    height: "auto", // Auto height for small screens
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    borderTopLeftRadius: "0px",
    borderBottomLeftRadius: "0px",
    borderTopRightRadius: isLast ? "14px" : "0px",
    borderBottomRightRadius: isLast ? "14px" : "0px",
    cursor: "pointer",
    "& .MuiCardContent-root": {
        padding: "16px",
        width: "100%",
    },
    // Small screens (xs, sm) - auto height
    "@media (max-width: 959px)": {
        height: "auto",
        borderTopLeftRadius: "14px",
        borderBottomLeftRadius: "14px",
        borderTopRightRadius: "14px",
        borderBottomRightRadius: "14px",
        marginBottom: "8px",
    },
    // Medium screens and up (md, lg, xl) - 5.5rem height
    "@media (min-width: 960px)": {
        height: "5.5rem",
    },
}));



const StyledTypographyCount = styled(Typography)({
    fontWeight: "700",
    fontSize: "26px",
    marginBottom: "8px",
});



const StyledTypographyName = styled(Typography)({
    fontSize: "13px",
    fontWeight: "600"
});



export const AlarmCardsContent = ({ data, caesarsCountData, verizonCountData, tmobileCountData, windsorCountData, factoryCountData }) => {
    const resultData = [];
    let temp = {
        images: [
            {
                index: 0,
                url: require("../../src/img/brand/Syracuse_University_2.png"),
            },
        ],
        alarms: data
    };
    let caesarsTemp = {
        images: [
            {
                index: 0,
                url: require("../../src/img/brand/caesars-nobu.png"),
            },
        ],
        alarms: caesarsCountData
    };
    let verizonTemp = {
        images: [
            {
                index: 0,
                url: require("../../src/img/brand/verizonipsecfoa.png"),
            },
        ],
        alarms: verizonCountData
    };
    let tmobileTemp = {
        images: [
            {
                index: 0,
                url: require("../../src/img/brand/tmobileipsecfoa.png"),
            },
        ],
        alarms: tmobileCountData
    };
    let windsorTemp = {
        images: [
            {
                index: 0,
                url: require("../../src/img/brand/windsor-hills-logo.png"),
            },
        ],
        alarms: windsorCountData
    };
    let factoryTemp = {
        images: [
            {
                index: 0,
                url: require("../../src/img/brand/tmobilemocnfoa.png"),
            },
        ],
        alarms: factoryCountData
    };
    resultData.push(temp);
    resultData.push(caesarsTemp);
    resultData.push(windsorTemp);
    resultData.push(verizonTemp);
    resultData.push(tmobileTemp);
    resultData.push(factoryTemp);
    // console.log("resultData changed", resultData);
    return (
        <Stack sx={{ flexGrow: 1, padding: { xs: "0px", sm: "16px", md: "16px", lg: "16px", xl: "16px" } }} spacing={3} mt={"0.5rem"}>
            {resultData?.map((item, itemIndex) => (
                <Grid container columnSpacing={{ xs: 0.6, md: 0.1 }} rowSpacing={{ xs: 1, md: 0 }} key={itemIndex}>
                    {item.images?.map((image, imageIndex) => (
                        <Grid
                            key={imageIndex}
                            size={{ xs: 12, sm: 12, md: 2.2, lg: 2.2, xl: 2.2 }}
                        >
                            <StyledImageAlarmCard bgcolor="#ffffff" imageIndex={imageIndex}>
                                <CardContent>
                                    <img
                                        alt="no"
                                        src={image.url}
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain',
                                            width: "150px"
                                        }}
                                    />
                                </CardContent>
                            </StyledImageAlarmCard>
                        </Grid>
                    ))}
                    {(() => {
                        const visibleAlarms = sortAlarms(item.alarms) || [];
                        return visibleAlarms.map((alarm, alarmIndex) => (
                        <Grid
                            key={alarmIndex}
                            size={{ xs: 6, sm: 6, md: 1.63, lg: 1.63, xl: 1.63 }}
                        >
                            <StyledAlarmCard
                                bgcolor={getColorByName(alarm.name)}
                                isLast={alarmIndex === visibleAlarms.length - 1}
                            >
                                <CardContent
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "column", md: "column" },
                                        justifyContent: { xs: "flex-start" },
                                        alignItems: { xs: "flex-start", sm: "flex-start", md: "flex-start" },
                                        gap: { xs: 1, sm: 1, md: 0 },
                                        rowSpacing: { xs: 1, sm: 1, md: 0 },
                                        padding: { xs: "10px", sm: "14px", md: "14px", lg: "14px" },
                                        width: "100%",
                                        "&:last-child": { paddingBottom: { xs: "14px", sm: "14px", md: "16px" } },
                                    }}
                                >
                                    <StyledTypographyCount
                                        variant="h5"
                                        component="div"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "26px",
                                        }}
                                    >
                                        {alarm.lastvalue}
                                    </StyledTypographyCount>

                                    <StyledTypographyName
                                        variant="body2"
                                        aria-label={`Alarm type: ${alarm.name}`}
                                        sx={{
                                            fontSize: "13px",
                                            opacity: 0.9,
                                            textAlign: "left",
                                        }}
                                    >
                                        {getAlarmName(alarm.name)}
                                    </StyledTypographyName>
                                </CardContent>
                            </StyledAlarmCard>
                        </Grid>
                        ));
                    })()}
                    {itemIndex === 0 && (
                        <Box
                            sx={{
                                width: 32,
                                height: 8,
                                bgcolor: "#a9b2bd",
                                borderRadius: "8px",
                                margin: "0 auto",
                                marginTop: "1rem",
                                display: { xs: "block", md: "none" } // Mobile only
                            }}
                        />
                    )}
                </Grid>))}
        </Stack >
    );
};